import { useMemo } from 'react';
import { css } from '@emotion/react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { BulkMessagingQueries } from '@frontend/api-bulk-messaging';
import { DashboardWidget, FirstWordBoldText, useDashboardWidget } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { ContentLoader, numeric } from '@frontend/design-system';
import { SingleBarChart } from './single-bar-chart';
import { SingleDoughnutChart } from './single-doughnut-chart';

interface BulkCampaignWidgetProps {
  campaignType: CampaignType;
}

const getRouteType = (campaignType: CampaignType): 'email' | 'text' => {
  switch (campaignType) {
    case CampaignType.EMAIL:
      return 'email';
    case CampaignType.SMS:
    default:
      return 'text';
  }
};

export const bulkCampaignWidgetSize = {
  extraSmall: 'tiny-slim',
  small: 'small-narrow',
  medium: 'small-wide',
  large: 'small-wide',
} as const;

export const BulkCampaignWidget = ({ campaignType }: BulkCampaignWidgetProps) => {
  const { t } = useTranslation('bulk-messaging');
  const navigate = useNavigate();
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { currentSize } = useDashboardWidget();

  const currentMonth = new Date().getMonth() + 1;
  const year = new Date().getFullYear();
  const { isLoading, data: usageData } = BulkMessagingQueries.useGetUsage(
    {
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      type: campaignType,
      year,
    },
    currentMonth
  );

  const { totalCount, sentAndFailedCount, scheduledCount } = useMemo(
    () =>
      Object.values(usageData ?? {}).reduce(
        (totals, locationUsage) => {
          const monthData = locationUsage.usage?.find((usage) => usage.month === currentMonth); // already filtering in the hook, but doing an extra check here just in case

          if (!monthData) {
            return totals;
          }

          return {
            totalCount: totals.totalCount + (monthData.allotment ?? 0),
            sentAndFailedCount: totals.sentAndFailedCount + (monthData.sent ?? 0) + (monthData.error ?? 0),
            scheduledCount: totals.scheduledCount + (monthData.scheduled ?? 0),
          };
        },
        {
          totalCount: 0,
          sentAndFailedCount: 0,
          scheduledCount: 0,
        }
      ),
    [currentMonth, usageData]
  );

  const remaining = totalCount - sentAndFailedCount - scheduledCount;

  const chartClickHandler = () => {
    navigate({
      to: '/bulk/$subNav/$type',
      params: {
        subNav: 'all-campaigns',
        type: getRouteType(campaignType),
      },
    });
  };

  const isTinySlimSize = currentSize === 'tiny-slim';

  return (
    <DashboardWidget>
      <DashboardWidget.Header />
      <DashboardWidget.Content css={[contentContainerStyle, { gap: theme.spacing(isTinySlimSize ? 0.5 : 2) }]}>
        <ContentLoader show={isLoading} />
        {isTinySlimSize ? (
          <SingleDoughnutChart
            remainingCount={remaining}
            scheduledCount={scheduledCount}
            sentCount={sentAndFailedCount}
            onClick={chartClickHandler}
          />
        ) : (
          <SingleBarChart
            remainingCount={remaining}
            scheduledCount={scheduledCount}
            sentCount={sentAndFailedCount}
            onClick={chartClickHandler}
          />
        )}
        <FirstWordBoldText
          css={{ strong: { fontSize: theme.fontSize(24), lineHeight: 1, color: theme.colors.neutral90 } }}
          color='light'
          size='medium'
        >
          {t('{{remainingCount}} Remaining', { remainingCount: numeric(remaining, { decimals: 0 }) })}
        </FirstWordBoldText>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

const contentContainerStyle = css({
  height: '100%',
  display: 'flex',
  alignItems: 'center',
});
